/**
 * @fileoverview Main Operator Administration Panel for 'Rasca y gana' (Scratcher - Tile ID 43).
 * Allows stadium operators to control projections, start/pause/reset matches with countdowns,
 * monitor real-time scratch progress and connection telemetry, manage themes, and view match history.
 * 
 * @module components/Admin/Games/Scratcher
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { Confirm } from './../../Commons/Modals/Confirm';
import { PremiumQR } from './../../Commons/PremiumQR';
import { ScratcherThemeModal } from './Scratcher/ScratcherThemeModal';

import {
    GetThemes,
    GetConfig,
    SaveConfig,
    GetMatchesHistory,
    DeleteTheme,
    CloneTheme,
    ShowCover,
    ShowGame,
    StartGame,
    PauseGame,
    ResumeGame,
    FinishEarly,
    ResetGame,
    GetRealtimeState
} from './../../../utilities/BL/Scratcher';

import {
    ConnectWebSocketForScratcherAdmin,
    DisconnectWebSocketForScratcherAdmin,
    SendWebSocketMessage
} from './../../../utilities/WS/Admin/ScratcherWS';

/**
 * Scratcher Operator Component (Tile ID 43).
 * 
 * @function Scratcher
 * @param {Object} props - Component properties.
 * @param {Function} props.getOutOfTheDynamic - Callback to exit dynamic back to dashboard.
 * @returns {React.ReactElement} Admin panel markup.
 */
export const Scratcher = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // Loading & Refresh states
    const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
    const [lastRefreshTimestampString, setLastRefreshTimestampString] = useState('');
    const [webSocketConnectionStatus, setWebSocketConnectionStatus] = useState('connecting');

    // Themes & Configuration
    const [availableThemesList, setAvailableThemesList] = useState([]);
    const [selectedThemeId, setSelectedThemeId] = useState(0);
    const [activeThemeObject, setActiveThemeObject] = useState(null);
    const [roomConfigState, setRoomConfigState] = useState({
        targetPercentage: 100,
        maxTimeSeconds: 20,
        coinSize: 25,
        gameMode: 'simultaneous'
    });

    // Real-time Game Telemetry State
    const [currentScratcherGameState, setCurrentScratcherGameState] = useState({
        state: 'idle', // idle, cover, game_ready, countdown, playing, paused, finished
        gameMode: 'simultaneous',
        targetPercentage: 100,
        maxTimeSeconds: 20,
        remainingTimeSeconds: 20,
        winner: null,
        effectiveDurationSeconds: 0,
        players: [
            {
                playerId: 'player1',
                slot: 'J1',
                name: 'Jugador 1',
                color: '#00ffcc',
                scratchedPercentage: 0,
                totalStrokes: 0,
                isCompleted: false,
                isConnected: false
            },
            {
                playerId: 'player2',
                slot: 'J2',
                name: 'Jugador 2',
                color: '#ff0055',
                scratchedPercentage: 0,
                totalStrokes: 0,
                isCompleted: false,
                isConnected: false
            }
        ]
    });

    // Countdown active count (3, 2, 1)
    const [countdownSecondsRemaining, setCountdownSecondsRemaining] = useState(null);

    // Live Player Names for Real-Time On-The-Fly Customization in Telemetry
    const [livePlayer1Name, setLivePlayer1Name] = useState('');
    const [livePlayer2Name, setLivePlayer2Name] = useState('');
    const nameUpdateTimeoutRef = useRef(null);
    const configSaveTimeoutRef = useRef(null);

    // Match History
    const [matchesHistoryList, setMatchesHistoryList] = useState([]);

    // Modals
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmModalConfiguration, setConfirmModalConfiguration] = useState({
        message: '',
        actionToExecute: null,
        yesButtonText: 'Sí, confirmar',
        noButtonText: 'Cancelar'
    });
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [themeToEditObject, setThemeToEditObject] = useState(null);
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [fanJoinQrUrl, setFanJoinQrUrl] = useState('');
    const [screenTargetDisplayUrl, setScreenTargetDisplayUrl] = useState('');

    /**
     * Resolves media path into an absolute preview URL.
     */
    const resolveMediaUrl = (mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:') || mediaPath.startsWith('data:')) {
            return mediaPath;
        }
        const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
        const cleanPath = mediaPath.startsWith('/') ? mediaPath : `/${mediaPath}`;
        return apiBaseUrl ? `${apiBaseUrl}${cleanPath}` : cleanPath;
    };

    /**
     * Handles incoming real-time WebSocket events for the operator.
     */
    const handleWebSocketMessageReceived = useCallback((receivedPacket) => {
        if (!receivedPacket || typeof receivedPacket !== 'object') return;

        const packetType = receivedPacket.type;

        switch (packetType) {
            case 'scratcher_progress_sync':
                if (receivedPacket.players && Array.isArray(receivedPacket.players)) {
                    setCurrentScratcherGameState((previousState) => ({
                        ...previousState,
                        players: receivedPacket.players
                    }));
                }
                break;

            case 'scratcher_countdown':
                setCountdownSecondsRemaining(receivedPacket.count);
                if (receivedPacket.state) {
                    setCurrentScratcherGameState(receivedPacket.state);
                }
                break;

            case 'scratcher_game_started':
                setCountdownSecondsRemaining(null);
                if (receivedPacket.state) {
                    setCurrentScratcherGameState(receivedPacket.state);
                }
                break;

            case 'scratcher_game_finished':
            case 'scratcher_game_over':
                setCountdownSecondsRemaining(null);
                if (receivedPacket.state) {
                    setCurrentScratcherGameState(receivedPacket.state);
                }
                loadMatchesHistory();
                break;

            case 'scratcher_update_names':
            case 'scratcher_set_player_names':
            case 'scratcher_players_updated':
            case 'scratcher_telemetry':
                if (receivedPacket.state) {
                    setCurrentScratcherGameState(receivedPacket.state);
                } else if (receivedPacket.players && Array.isArray(receivedPacket.players)) {
                    setCurrentScratcherGameState((prev) => ({
                        ...prev,
                        players: receivedPacket.players
                    }));
                }
                break;

            case 'scratcher_show_cover':
            case 'scratcher_show_game':
            case 'scratcher_game_paused':
            case 'scratcher_game_resumed':
            case 'scratcher_game_reset':
            case 'scratcher_reset':
            case 'scratcher_state_snapshot':
            case 'scratcher_player_joined':
            case 'scratcher_player_left':
                if (receivedPacket.state) {
                    setCurrentScratcherGameState(receivedPacket.state);
                }
                break;

            default:
                break;
        }
    }, []);

    /**
     * Initializes data and WebSocket listeners on component mount.
     */
    useEffect(() => {
        loadAllInitialAdminData();

        ConnectWebSocketForScratcherAdmin(
            handleWebSocketMessageReceived,
            (newConnectionStatus) => setWebSocketConnectionStatus(newConnectionStatus)
        );

        return () => {
            DisconnectWebSocketForScratcherAdmin();
        };
    }, [handleWebSocketMessageReceived]);

    /**
     * Fetches all themes, room configuration, real-time snapshot and match history.
     */
    const loadAllInitialAdminData = async () => {
        setIsLoadingInitialData(true);
        try {
            // 1. Fetch Themes
            const themesResponse = await GetThemes();
            if (themesResponse.success && Array.isArray(themesResponse.data)) {
                setAvailableThemesList(themesResponse.data);
            }

            // 2. Fetch Room Configuration
            const configResponse = await GetConfig();
            if (configResponse.success && configResponse.data) {
                const configData = configResponse.data;
                setRoomConfigState({
                    targetPercentage: configData.targetPercentage || 100,
                    maxTimeSeconds: configData.maxTimeSeconds || 20,
                    coinSize: configData.coinSize || 25,
                    gameMode: configData.gameMode || 'simultaneous'
                });

                if (configData.activeThemeId) {
                    setSelectedThemeId(configData.activeThemeId);
                } else if (themesResponse.success && themesResponse.data?.length > 0) {
                    setSelectedThemeId(themesResponse.data[0].id);
                }

                if (configData.activeTheme) {
                    setActiveThemeObject(configData.activeTheme);
                }
            }

            // 3. Fetch Real-time Engine Snapshot
            const realtimeResponse = await GetRealtimeState();
            if (realtimeResponse.success && realtimeResponse.data) {
                setCurrentScratcherGameState(realtimeResponse.data);
                if (realtimeResponse.data.activeTheme) {
                    setActiveThemeObject(realtimeResponse.data.activeTheme);
                }
            }

            // 4. Fetch History
            await loadMatchesHistory();
        } catch (initializationError) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cargar los datos de administración de Rasca y Gana.' });
        } finally {
            setIsLoadingInitialData(false);
            setLastRefreshTimestampString(
                new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
            );
        }
    };

    /**
     * Loads the match history log from database.
     */
    const loadMatchesHistory = async () => {
        try {
            const historyResponse = await GetMatchesHistory(25);
            if (historyResponse.success && Array.isArray(historyResponse.data)) {
                setMatchesHistoryList(historyResponse.data);
            }
        } catch (historyException) {
            // Non-blocking log fetch
        }
    };

    /**
     * Validates whether a theme is actively selected before proceeding.
     */
    const checkThemeSelected = () => {
        if (!selectedThemeId || Number(selectedThemeId) === 0 || selectedThemeId === '') {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Selecciona un tema para continuar.' });
            return false;
        }
        return true;
    };

    /**
     * Updates the active theme in room config and persists.
     */
    const handleActiveThemeSelectionChange = async (event) => {
        const rawValue = event.target.value;
        const newlySelectedId = rawValue ? Number(rawValue) : 0;
        setSelectedThemeId(newlySelectedId);

        const foundTheme = availableThemesList.find((themeItem) => themeItem.id === newlySelectedId);
        setActiveThemeObject(foundTheme || null);

        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando tema activo...' });
        const saveConfigResponse = await SaveConfig({
            activeThemeId: newlySelectedId > 0 ? newlySelectedId : null,
            targetPercentage: roomConfigState.targetPercentage,
            maxTimeSeconds: roomConfigState.maxTimeSeconds,
            coinSize: roomConfigState.coinSize,
            gameMode: roomConfigState.gameMode
        });

        if (saveConfigResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema activo actualizado correctamente.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al actualizar el tema activo.' });
        }
    };

    /**
     * Updates target percentage or max time seconds with debounced persistence (400ms).
     */
    const handleConfigParameterChange = (parameterName, parameterValue) => {
        const updatedConfig = {
            ...roomConfigState,
            [parameterName]: parameterValue
        };
        setRoomConfigState(updatedConfig);

        if (configSaveTimeoutRef.current) {
            clearTimeout(configSaveTimeoutRef.current);
        }

        configSaveTimeoutRef.current = setTimeout(async () => {
            try {
                await SaveConfig({
                    activeThemeId: selectedThemeId > 0 ? selectedThemeId : null,
                    targetPercentage: updatedConfig.targetPercentage,
                    maxTimeSeconds: updatedConfig.maxTimeSeconds,
                    coinSize: updatedConfig.coinSize,
                    gameMode: updatedConfig.gameMode
                });
            } catch (saveError) {
                // Non-blocking save error
            }
        }, 400);
    };

    /**
     * Updates live visible names for players and broadcasts via WebSocket.
     */
    const handleUpdateLiveName = (slot, newName) => {
        if (slot === 'player1') setLivePlayer1Name(newName);
        if (slot === 'player2') setLivePlayer2Name(newName);

        if (nameUpdateTimeoutRef.current) {
            clearTimeout(nameUpdateTimeoutRef.current);
        }

        nameUpdateTimeoutRef.current = setTimeout(() => {
            const p1 = slot === 'player1' ? newName : (livePlayer1Name || player1.name);
            const p2 = slot === 'player2' ? newName : (livePlayer2Name || player2.name);
            SendWebSocketMessage({ type: 'scratcher_update_names', player1Name: p1, player2Name: p2 });
        }, 300);
    };

    // ==========================================
    // PROJECTION CONTROLS
    // ==========================================

    const handleShowCoverAction = async () => {
        if (!checkThemeSelected()) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Proyectando Portada de Rasca y Gana...' });
        const coverResponse = await ShowCover();
        if (coverResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Portada emitida a pantalla gigante.' });
            if (coverResponse.data) setCurrentScratcherGameState(coverResponse.data);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: coverResponse.message || 'Error al emitir portada.' });
        }
    };

    const handleShowGameAction = async () => {
        if (!checkThemeSelected()) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Proyectando Escenario de Raspado...' });
        const gameResponse = await ShowGame();
        if (gameResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Escenario de juego proyectado en pantalla.' });
            if (gameResponse.data) setCurrentScratcherGameState(gameResponse.data);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: gameResponse.message || 'Error al proyectar escenario.' });
        }
    };

    const handleOpenQrModal = () => {
        if (!checkThemeSelected()) return;
        const currentHostname = window.location.hostname;
        const currentPort = window.location.port ? `:${window.location.port}` : '';
        const currentProtocol = window.location.protocol;

        const fanUrl = `${currentProtocol}//${currentHostname}${currentPort}/scratcher`;
        const screenUrl = `${currentProtocol}//${currentHostname}${currentPort}/screen`;

        setFanJoinQrUrl(fanUrl);
        setScreenTargetDisplayUrl(screenUrl);
        setIsQrModalOpen(true);
    };

    // ==========================================
    // MATCH CONTROLS
    // ==========================================

    const handleStartGameAction = async () => {
        if (!checkThemeSelected()) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Iniciando partida con cuenta regresiva...' });
        const startResponse = await StartGame(
            roomConfigState.maxTimeSeconds,
            roomConfigState.targetPercentage,
            roomConfigState.gameMode,
            selectedThemeId,
            null,
            roomConfigState.coinSize
        );
        if (startResponse.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡Partida en curso!' });
            if (startResponse.data) setCurrentScratcherGameState(startResponse.data);
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: startResponse.message || 'Error al iniciar partida.' });
        }
    };

    const handleTogglePauseAction = async () => {
        if (currentScratcherGameState.state === 'playing') {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Pausando partida...' });
            const pauseResponse = await PauseGame();
            if (pauseResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Partida pausada.' });
                if (pauseResponse.data) setCurrentScratcherGameState(pauseResponse.data);
            }
        } else if (currentScratcherGameState.state === 'paused') {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reanudando partida...' });
            const resumeResponse = await ResumeGame();
            if (resumeResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Partida reanudada.' });
                if (resumeResponse.data) setCurrentScratcherGameState(resumeResponse.data);
            }
        }
    };

    const handleConfirmFinishEarly = () => {
        setConfirmModalConfiguration({
            message: '¿Estás seguro de finalizar la partida antes de tiempo? Se declarará ganador de inmediato con el mayor porcentaje raspado.',
            yesButtonText: 'Sí, finalizar ahora',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Deteniendo partida de emergencia...' });
                const finishResponse = await FinishEarly();
                if (finishResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Partida concluida anticipadamente.' });
                    if (finishResponse.data) setCurrentScratcherGameState(finishResponse.data);
                    await loadMatchesHistory();
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: finishResponse.message || 'Error al finalizar.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    const handleConfirmResetGame = () => {
        setConfirmModalConfiguration({
            message: '¿Estás seguro de reiniciar la dinámica a estado de reposo? Se borrarán los porcentajes de la ronda activa.',
            yesButtonText: 'Sí, reiniciar',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reiniciando dinámica...' });
                const resetResponse = await ResetGame();
                if (resetResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Dinámica reiniciada a estado de espera.' });
                    if (resetResponse.data) setCurrentScratcherGameState(resetResponse.data);
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: resetResponse.message || 'Error al reiniciar.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    // ==========================================
    // THEME CRUD ACTIONS
    // ==========================================

    const handleOpenCreateThemeModal = () => {
        setThemeToEditObject(null);
        setIsThemeModalOpen(true);
    };

    const handleOpenEditThemeModal = () => {
        const foundTheme = availableThemesList.find((item) => item.id === selectedThemeId);
        setThemeToEditObject(foundTheme || null);
        setIsThemeModalOpen(true);
    };

    const handleCloneSelectedTheme = async (themeIdToClone = null) => {
        const targetId = themeIdToClone || selectedThemeId;
        if (!targetId || targetId <= 0) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Clonando tema...' });
        try {
            const cloneResponse = await CloneTheme(targetId);
            if (cloneResponse.success && cloneResponse.data) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema clonado exitosamente.' });
                await loadAllInitialAdminData();
                setSelectedThemeId(cloneResponse.data.id);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: cloneResponse.message || 'Error al clonar tema.' });
            }
        } catch (cloneError) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al clonar tema.' });
        }
    };

    const handleConfirmDeleteTheme = (themeItemToDelete = null) => {
        const targetTheme = themeItemToDelete || availableThemesList.find((t) => t.id === selectedThemeId);
        if (!targetTheme || !targetTheme.id) return;

        setConfirmModalConfiguration({
            message: `¿Estás seguro de que deseas eliminar permanentemente el tema "${targetTheme.name}"?`,
            yesButtonText: 'Sí, eliminar',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Eliminando tema...' });
                const deleteResponse = await DeleteTheme(targetTheme.id);
                if (deleteResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema eliminado correctamente.' });
                    await loadAllInitialAdminData();
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: deleteResponse.message || 'Error al eliminar.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    // Helper for player list
    const player1 = currentScratcherGameState.players?.find((p) => p.playerId === 'player1' || p.slot === 'J1') || {
        name: activeThemeObject?.player1Name || 'Jugador 1',
        scratchedPercentage: 0,
        totalStrokes: 0,
        isConnected: false,
        color: activeThemeObject?.player1Color || '#00ffcc'
    };

    const player2 = currentScratcherGameState.players?.find((p) => p.playerId === 'player2' || p.slot === 'J2') || {
        name: activeThemeObject?.player2Name || 'Jugador 2',
        scratchedPercentage: 0,
        totalStrokes: 0,
        isConnected: false,
        color: activeThemeObject?.player2Color || '#ff0055'
    };

    const extraPlayers = currentScratcherGameState.players?.filter((p) => p.playerId !== 'player1' && p.slot !== 'J1' && p.playerId !== 'player2' && p.slot !== 'J2') || [];

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>
            <div className="container-fluid py-2 text-dark">
                
                {/* 1. TOP STATUS BAR */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 bg-white p-3 rounded-4 shadow-sm border">
                    <div className="d-flex align-items-center">
                        <i className="bi bi-hand-index-thumb-fill text-warning fs-2 me-3"></i>
                        <div>
                            <h4 className="fw-bold mb-0 text-dark">Panel de Operador: Rasca y Gana</h4>
                            <small className="text-secondary">Control en vivo, telemetría autoritativa y configuración de temas (Tile 43)</small>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-3 mt-2 mt-md-0">
                        {/* WebSocket Status Pill */}
                        <div className="d-flex align-items-center bg-light px-3 py-1 rounded-pill border">
                            <span
                                className={`badge rounded-circle p-1 me-2 ${
                                    webSocketConnectionStatus === 'connected'
                                        ? 'bg-success'
                                        : webSocketConnectionStatus === 'connecting' || webSocketConnectionStatus === 'reconnecting'
                                        ? 'bg-warning'
                                        : 'bg-danger'
                                }`}
                                style={{ width: '10px', height: '10px', display: 'inline-block' }}
                            ></span>
                            <span className="small fw-bold text-secondary text-capitalize">
                                WS: {webSocketConnectionStatus === 'connected' ? 'En Vivo' : webSocketConnectionStatus}
                            </span>
                        </div>

                        {/* Timestamp */}
                        {lastRefreshTimestampString && (
                            <small className="text-muted d-none d-sm-inline">
                                <i className="bi bi-clock-history me-1"></i>
                                {lastRefreshTimestampString}
                            </small>
                        )}

                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={loadAllInitialAdminData}
                            disabled={isLoadingInitialData}
                            title="Recargar datos"
                        >
                            <i className={`bi bi-arrow-clockwise ${isLoadingInitialData ? 'spin' : ''}`}></i>
                        </button>
                    </div>
                </div>

                {/* 2. MAIN CONTROLS ROW (PROJECTION & MATCH CONTROLS) */}
                <div className="row g-4 mb-4">
                    
                    {/* CARD 1: PROJECTION CONTROLS */}
                    <div className="col-lg-5">
                        <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden">
                            <div className="card-header bg-dark text-white py-3 px-4 d-flex justify-content-between align-items-center">
                                <span className="fw-bold">
                                    <i className="bi bi-display me-2 text-warning"></i> Control de Proyección
                                </span>
                                <span className={`badge ${currentScratcherGameState.state === 'cover' ? 'bg-primary' : currentScratcherGameState.state === 'game_ready' || currentScratcherGameState.state === 'playing' ? 'bg-success' : 'bg-secondary'}`}>
                                    {currentScratcherGameState.state.toUpperCase()}
                                </span>
                            </div>
                            <div className="card-body p-4 d-flex flex-column justify-content-between">
                                <p className="text-muted small mb-4">
                                    Emite las pantallas de portada o de escenario interactivo hacia la pantalla gigante del estadio y overlays de transmisión.
                                </p>

                                <div className="d-grid gap-3">
                                    <button
                                        type="button"
                                        className={`btn py-2 fw-bold text-uppercase shadow-sm ${currentScratcherGameState.state === 'cover' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={handleShowCoverAction}
                                    >
                                        <i className="bi bi-card-image me-2"></i> 1. Mostrar Portada
                                    </button>

                                    <button
                                        type="button"
                                        className={`btn py-2 fw-bold text-uppercase shadow-sm ${currentScratcherGameState.state === 'game_ready' ? 'btn-success text-white' : 'btn-outline-success'}`}
                                        onClick={handleShowGameAction}
                                    >
                                        <i className="bi bi-tv me-2"></i> 2. Proyectar Pantalla de Juego
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-warning py-2 fw-bold text-dark text-uppercase shadow-sm"
                                        onClick={handleOpenQrModal}
                                    >
                                        <i className="bi bi-qr-code-scan me-2"></i> Ver Instrucciones (QR)
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CARD 2: MATCH CONTROL */}
                    <div className="col-lg-7">
                        <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden">
                            <div className="card-header bg-dark text-white py-3 px-4 d-flex justify-content-between align-items-center">
                                <span className="fw-bold">
                                    <i className="bi bi-controller me-2 text-warning"></i> Control de Partida
                                </span>
                                {countdownSecondsRemaining !== null && (
                                    <span className="badge bg-danger animate__animated animate__pulse animate__infinite">
                                        ¡ARRANCANDO EN {countdownSecondsRemaining}!
                                    </span>
                                )}
                            </div>
                            <div className="card-body p-4">
                                
                                {/* Theme Selector */}
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label className="form-label fw-bold small text-dark mb-0">
                                            Tema Visual Activo
                                        </label>
                                        {selectedThemeId > 0 && (
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 small fw-bold text-decoration-none"
                                                onClick={handleOpenEditThemeModal}
                                            >
                                                <i className="bi bi-pencil-square me-1"></i>Editar este tema
                                            </button>
                                        )}
                                    </div>
                                    <select
                                        className="form-select fw-bold"
                                        value={selectedThemeId || ''}
                                        onChange={handleActiveThemeSelectionChange}
                                    >
                                        <option value="0">-- Seleccionar tema visual --</option>
                                        {availableThemesList.map((themeItem) => (
                                            <option key={themeItem.id} value={themeItem.id}>
                                                {themeItem.name} ({themeItem.fontFamily || 'Bangers'})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Match Parameters (Target %, Max Time & Coin Size with Direct Inputs & Sliders) */}
                                <div className="row g-3 mb-4">
                                    <div className="col-md-4">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <label className="form-label fw-bold small text-dark mb-0">Meta Raspado</label>
                                            <div className="input-group input-group-sm" style={{ width: '85px' }}>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-end fw-bold px-1"
                                                    min="10"
                                                    max="100"
                                                    value={roomConfigState.targetPercentage}
                                                    onChange={(e) => handleConfigParameterChange('targetPercentage', Math.min(100, Math.max(1, Number(e.target.value))))}
                                                    disabled={currentScratcherGameState.state === 'playing'}
                                                />
                                                <span className="input-group-text bg-primary text-white fw-bold px-2">%</span>
                                            </div>
                                        </div>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min="10"
                                            max="100"
                                            step="5"
                                            value={roomConfigState.targetPercentage}
                                            onChange={(e) => handleConfigParameterChange('targetPercentage', Number(e.target.value))}
                                            disabled={currentScratcherGameState.state === 'playing'}
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <label className="form-label fw-bold small text-dark mb-0">Tiempo Límite</label>
                                            <div className="input-group input-group-sm" style={{ width: '85px' }}>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-end fw-bold px-1"
                                                    min="5"
                                                    max="300"
                                                    value={roomConfigState.maxTimeSeconds}
                                                    onChange={(e) => handleConfigParameterChange('maxTimeSeconds', Math.max(1, Number(e.target.value)))}
                                                    disabled={currentScratcherGameState.state === 'playing'}
                                                />
                                                <span className="input-group-text bg-danger text-white fw-bold px-2">s</span>
                                            </div>
                                        </div>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min="5"
                                            max="120"
                                            step="1"
                                            value={roomConfigState.maxTimeSeconds}
                                            onChange={(e) => handleConfigParameterChange('maxTimeSeconds', Number(e.target.value))}
                                            disabled={currentScratcherGameState.state === 'playing'}
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <label className="form-label fw-bold small text-dark mb-0">Grosor Pincel</label>
                                            <div className="input-group input-group-sm" style={{ width: '85px' }}>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm text-end fw-bold px-1"
                                                    min="5"
                                                    max="50"
                                                    value={roomConfigState.coinSize || 25}
                                                    onChange={(e) => handleConfigParameterChange('coinSize', Math.min(50, Math.max(5, Number(e.target.value))))}
                                                    disabled={currentScratcherGameState.state === 'playing'}
                                                />
                                                <span className="input-group-text bg-warning text-dark fw-bold px-2">px</span>
                                            </div>
                                        </div>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min="5"
                                            max="50"
                                            step="1"
                                            value={roomConfigState.coinSize || 25}
                                            onChange={(e) => handleConfigParameterChange('coinSize', Number(e.target.value))}
                                            disabled={currentScratcherGameState.state === 'playing'}
                                        />
                                    </div>
                                </div>

                                {/* ACTION BUTTONS (INICIAR / REINICIAR Y BOTÓN DEDICADO DETENER Y MOSTRAR GANADOR) */}
                                <div className="row g-2">
                                    <div className="col-sm-6">
                                        {currentScratcherGameState.state === 'finished' ? (
                                            <button
                                                type="button"
                                                className="btn btn-primary w-100 py-2 fw-bold text-uppercase shadow-sm"
                                                onClick={handleConfirmResetGame}
                                            >
                                                <i className="bi bi-arrow-counterclockwise me-1"></i> Reiniciar Partida
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn btn-success w-100 py-2 fw-bold text-uppercase shadow-sm"
                                                onClick={handleStartGameAction}
                                                disabled={currentScratcherGameState.state === 'playing' || countdownSecondsRemaining !== null}
                                            >
                                                <i className="bi bi-play-fill me-1"></i> Iniciar Partida
                                            </button>
                                        )}
                                    </div>

                                    <div className="col-sm-6">
                                        <button
                                            type="button"
                                            className="btn btn-danger w-100 py-2 fw-bold text-uppercase shadow-sm"
                                            onClick={handleConfirmFinishEarly}
                                            disabled={currentScratcherGameState.state !== 'playing' && currentScratcherGameState.state !== 'paused' && currentScratcherGameState.state !== 'countdown'}
                                        >
                                            <i className="bi bi-stop-circle-fill me-1"></i> Detener y Mostrar Ganador
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                </div>

                {/* 3. REAL-TIME TELEMETRY MONITOR (J1 vs J2 & N PLAYERS) */}
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                    <div className="card-header bg-dark text-white py-3 px-4 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <i className="bi bi-speedometer2 text-warning fs-5 me-2"></i>
                            <span className="fw-bold">Monitor de Telemetría en Tiempo Real (J1 vs J2)</span>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <span className="badge bg-secondary">
                                Cronómetro: {currentScratcherGameState.remainingTimeSeconds || 0}s / {currentScratcherGameState.maxTimeSeconds || 20}s
                            </span>
                            {currentScratcherGameState.winner && (
                                <span className="badge bg-warning text-dark fw-bold">
                                    🏆 Ganador: {currentScratcherGameState.winner}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="card-body p-4 bg-light">
                        <div className="row g-4 justify-content-center">
                            
                            {/* JUGADOR 1 (J1) CARD */}
                            <div className="col-md-6 col-lg-5">
                                <div
                                    className="card border-0 shadow rounded-4 p-3 h-100 bg-white"
                                    style={{ borderTop: `6px solid ${player1.color || '#00ffcc'}` }}
                                >
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            {activeThemeObject?.player1Avatar ? (
                                                <img
                                                    src={resolveMediaUrl(activeThemeObject.player1Avatar)}
                                                    alt="J1"
                                                    className="rounded-circle me-2 border"
                                                    style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                                />
                                            ) : (
                                                <div
                                                    className="rounded-circle me-2 d-flex align-items-center justify-content-center text-white fw-bold"
                                                    style={{ width: '40px', height: '40px', backgroundColor: player1.color || '#00ffcc' }}
                                                >
                                                    J1
                                                </div>
                                            )}
                                            <div>
                                                <h5 className="fw-bold mb-0 text-dark">{player1.name || 'Jugador 1'}</h5>
                                                <small className="text-muted">Ranura J1</small>
                                            </div>
                                        </div>

                                        <span className={`badge rounded-pill ${player1.isConnected ? 'bg-success' : 'bg-secondary'}`}>
                                            <i className={`bi ${player1.isConnected ? 'bi-wifi' : 'bi-wifi-off'} me-1`}></i>
                                            {player1.isConnected ? 'Conectado' : 'Desconectado'}
                                        </span>
                                    </div>

                                    {/* Progress Bar & Percentage */}
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-end mb-1">
                                            <span className="small text-muted fw-bold">ÁREA RASPADA:</span>
                                            <span className="fs-3 fw-bold" style={{ color: player1.color || '#00ffcc' }}>
                                                {Number(player1.scratchedPercentage || 0).toFixed(1)}%
                                            </span>
                                        </div>
                                        <div className="progress" style={{ height: '22px' }}>
                                            <div
                                                className="progress-bar progress-bar-striped progress-bar-animated fw-bold text-dark"
                                                role="progressbar"
                                                style={{
                                                    width: `${Math.min(100, player1.scratchedPercentage || 0)}%`,
                                                    backgroundColor: player1.color || '#00ffcc'
                                                }}
                                            >
                                                {Number(player1.scratchedPercentage || 0).toFixed(0)}%
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between small text-muted border-top pt-2">
                                        <span><i className="bi bi-hand-index-thumb me-1"></i>Trazos: <strong>{player1.totalStrokes || 0}</strong></span>
                                        <span>Meta: <strong>{currentScratcherGameState.targetPercentage || 100}%</strong></span>
                                        {player1.isCompleted && <span className="badge bg-success">¡META ALCANZADA!</span>}
                                    </div>

                                    {/* LIVE NAME INPUT J1 */}
                                    <div className="mt-2 pt-2 border-top">
                                        <label className="form-label small text-muted fw-bold mb-1">
                                            <i className="bi bi-pencil-fill me-1"></i> Nombre en Vivo (J1):
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Nombre de participante J1"
                                            value={livePlayer1Name !== '' ? livePlayer1Name : (player1.name || '')}
                                            onChange={(e) => handleUpdateLiveName('player1', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* JUGADOR 2 (J2) CARD */}
                            <div className="col-md-6 col-lg-5">
                                <div
                                    className="card border-0 shadow rounded-4 p-3 h-100 bg-white"
                                    style={{ borderTop: `6px solid ${player2.color || '#ff0055'}` }}
                                >
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            {activeThemeObject?.player2Avatar ? (
                                                <img
                                                    src={resolveMediaUrl(activeThemeObject.player2Avatar)}
                                                    alt="J2"
                                                    className="rounded-circle me-2 border"
                                                    style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                                />
                                            ) : (
                                                <div
                                                    className="rounded-circle me-2 d-flex align-items-center justify-content-center text-white fw-bold"
                                                    style={{ width: '40px', height: '40px', backgroundColor: player2.color || '#ff0055' }}
                                                >
                                                    J2
                                                </div>
                                            )}
                                            <div>
                                                <h5 className="fw-bold mb-0 text-dark">{player2.name || 'Jugador 2'}</h5>
                                                <small className="text-muted">Ranura J2</small>
                                            </div>
                                        </div>

                                        <span className={`badge rounded-pill ${player2.isConnected ? 'bg-success' : 'bg-secondary'}`}>
                                            <i className={`bi ${player2.isConnected ? 'bi-wifi' : 'bi-wifi-off'} me-1`}></i>
                                            {player2.isConnected ? 'Conectado' : 'Desconectado'}
                                        </span>
                                    </div>

                                    {/* Progress Bar & Percentage */}
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-end mb-1">
                                            <span className="small text-muted fw-bold">ÁREA RASPADA:</span>
                                            <span className="fs-3 fw-bold" style={{ color: player2.color || '#ff0055' }}>
                                                {Number(player2.scratchedPercentage || 0).toFixed(1)}%
                                            </span>
                                        </div>
                                        <div className="progress" style={{ height: '22px' }}>
                                            <div
                                                className="progress-bar progress-bar-striped progress-bar-animated fw-bold text-white"
                                                role="progressbar"
                                                style={{
                                                    width: `${Math.min(100, player2.scratchedPercentage || 0)}%`,
                                                    backgroundColor: player2.color || '#ff0055'
                                                }}
                                            >
                                                {Number(player2.scratchedPercentage || 0).toFixed(0)}%
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between small text-muted border-top pt-2">
                                        <span><i className="bi bi-hand-index-thumb me-1"></i>Trazos: <strong>{player2.totalStrokes || 0}</strong></span>
                                        <span>Meta: <strong>{currentScratcherGameState.targetPercentage || 100}%</strong></span>
                                        {player2.isCompleted && <span className="badge bg-success">¡META ALCANZADA!</span>}
                                    </div>

                                    {/* LIVE NAME INPUT J2 */}
                                    <div className="mt-2 pt-2 border-top">
                                        <label className="form-label small text-muted fw-bold mb-1">
                                            <i className="bi bi-pencil-fill me-1"></i> Nombre en Vivo (J2):
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Nombre de participante J2"
                                            value={livePlayer2Name !== '' ? livePlayer2Name : (player2.name || '')}
                                            onChange={(e) => handleUpdateLiveName('player2', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* Extra Players Render (if any) */}
                        {extraPlayers.length > 0 && (
                            <div className="row g-3 justify-content-center mt-3">
                                {extraPlayers.map((extraP, extraIdx) => (
                                    <div key={extraIdx} className="col-md-4">
                                        <div className="card p-2 bg-white rounded-3 shadow-sm border" style={{ borderLeft: `5px solid ${extraP.color || '#aa00ff'}` }}>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className="fw-bold small">{extraP.name || `J${extraIdx + 3}`}</span>
                                                <span className="badge bg-secondary">{extraP.isConnected ? 'Conectado' : 'Offline'}</span>
                                            </div>
                                            <div className="progress" style={{ height: '14px' }}>
                                                <div
                                                    className="progress-bar"
                                                    style={{ width: `${extraP.scratchedPercentage || 0}%`, backgroundColor: extraP.color || '#aa00ff' }}
                                                >
                                                    {Number(extraP.scratchedPercentage || 0).toFixed(0)}%
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* 4. INDEPENDENT COLLAPSIBLE SECTIONS: THEME CATALOG & MATCH HISTORY */}
                <div className="d-flex flex-column gap-3 mb-4">
                    
                    {/* CARD 1: THEME CATALOG */}
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="card-header bg-white py-3 px-4 d-flex justify-content-between align-items-center">
                            <button
                                className="btn btn-link text-decoration-none p-0 fw-bold text-dark fs-5 d-flex align-items-center"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#collapseCatalog"
                                aria-expanded="true"
                                aria-controls="collapseCatalog"
                            >
                                <i className="bi bi-collection-fill text-primary me-2"></i> Catálogo de Temas de Rasca y Gana ({availableThemesList.length})
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary fw-bold shadow-sm"
                                onClick={handleOpenCreateThemeModal}
                            >
                                <i className="bi bi-plus-circle-fill me-1"></i> Crear Nuevo Tema
                            </button>
                        </div>
                        <div
                            id="collapseCatalog"
                            className="collapse show"
                        >
                            <div className="card-body p-4 bg-light border-top">
                                {availableThemesList.length === 0 ? (
                                    <div className="text-center py-4 bg-white rounded-3 border">
                                        <i className="bi bi-folder2-open text-muted fs-1 mb-2 d-block"></i>
                                        <p className="text-muted mb-0">No hay temas configurados todavía.</p>
                                    </div>
                                ) : (
                                    <div className="row g-3">
                                        {availableThemesList.map((themeItem) => (
                                            <div key={themeItem.id} className="col-md-6 col-lg-4">
                                                <div className={`card h-100 border-0 rounded-4 shadow-sm p-3 bg-white ${selectedThemeId === themeItem.id ? 'border border-2 border-primary' : ''}`}>
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h6 className="fw-bold text-dark mb-0 text-truncate">{themeItem.name}</h6>
                                                        {selectedThemeId === themeItem.id && (
                                                            <span className="badge bg-primary">ACTIVO</span>
                                                        )}
                                                    </div>

                                                    <div className="small text-muted mb-3">
                                                        <div><i className="bi bi-type me-1"></i>Fuente: <strong>{themeItem.fontFamily || 'Bangers'}</strong></div>
                                                        <div><i className="bi bi-disc me-1"></i>Grosor Moneda: <strong>{themeItem.coinSize || 25}px</strong></div>
                                                        <div><i className="bi bi-trophy me-1"></i>Meta: <strong>{themeItem.targetPercentage || 100}%</strong> | ⏱ <strong>{themeItem.maxTimeSeconds || 20}s</strong></div>
                                                    </div>

                                                    <div className="d-flex gap-2 mt-auto">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary flex-fill"
                                                            onClick={() => {
                                                                setThemeToEditObject(themeItem);
                                                                setIsThemeModalOpen(true);
                                                            }}
                                                            title="Editar Tema"
                                                        >
                                                            <i className="bi bi-pencil me-1"></i>Editar
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => handleCloneSelectedTheme(themeItem.id)}
                                                            title="Clonar Tema"
                                                        >
                                                            <i className="bi bi-copy"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleConfirmDeleteTheme(themeItem)}
                                                            title="Eliminar Tema"
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* CARD 2: MATCH HISTORY */}
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="card-header bg-white py-3 px-4 d-flex justify-content-between align-items-center">
                            <button
                                className="btn btn-link text-decoration-none p-0 fw-bold text-dark fs-5 d-flex align-items-center"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#collapseHistory"
                                aria-expanded="false"
                                aria-controls="collapseHistory"
                            >
                                <i className="bi bi-clock-history text-secondary me-2"></i> Historial de Partidas Jugadas ({matchesHistoryList.length})
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={loadMatchesHistory}
                            >
                                <i className="bi bi-arrow-clockwise me-1"></i> Actualizar Historial
                            </button>
                        </div>
                        <div
                            id="collapseHistory"
                            className="collapse"
                        >
                            <div className="card-body p-4 bg-light border-top">
                                {matchesHistoryList.length === 0 ? (
                                    <div className="text-center py-4 bg-white rounded-3 border">
                                        <i className="bi bi-inbox text-muted fs-1 mb-2 d-block"></i>
                                        <p className="text-muted mb-0">No hay registros de partidas jugadas aún.</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive bg-white rounded-3 shadow-sm border">
                                        <table className="table table-hover align-middle mb-0 small">
                                            <thead className="table-dark">
                                                <tr>
                                                    <th>Fecha / Hora</th>
                                                    <th>Tema</th>
                                                    <th>Ganador</th>
                                                    <th>Duración</th>
                                                    <th>% Raspado J1</th>
                                                    <th>% Raspado J2</th>
                                                    <th>Trazos (J1 / J2)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {matchesHistoryList.map((matchItem) => (
                                                    <tr key={matchItem.id}>
                                                        <td>{new Date(matchItem.playedAt).toLocaleString()}</td>
                                                        <td className="fw-bold">{matchItem.themeName || `Tema #${matchItem.themeId || '-'}`}</td>
                                                        <td>
                                                            <span className="badge bg-warning text-dark fw-bold">
                                                                {matchItem.winner || 'Sin Ganador'}
                                                            </span>
                                                        </td>
                                                        <td>{matchItem.effectiveDurationSeconds}s</td>
                                                        <td className="fw-bold text-info">
                                                            {Number(matchItem.scratchedPercentageJ1 || 0).toFixed(1)}%
                                                        </td>
                                                        <td className="fw-bold text-danger">
                                                            {Number(matchItem.scratchedPercentageJ2 || 0).toFixed(1)}%
                                                        </td>
                                                        <td>{matchItem.totalStrokesJ1 || 0} / {matchItem.totalStrokesJ2 || 0}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* THEME MODAL */}
                <ScratcherThemeModal
                    isOpen={isThemeModalOpen}
                    onCloseModal={() => setIsThemeModalOpen(false)}
                    initialThemeData={themeToEditObject}
                    onThemeSavedSuccessfully={async (savedTheme) => {
                        await loadAllInitialAdminData();
                        if (savedTheme && savedTheme.id) {
                            setSelectedThemeId(savedTheme.id);
                        }
                    }}
                />

                {/* CONFIRM MODAL */}
                {isConfirmModalOpen && (
                    <Confirm
                        message={confirmModalConfiguration.message}
                        textYesButton={confirmModalConfiguration.yesButtonText}
                        textNoButton={confirmModalConfiguration.noButtonText}
                        actionToYesButton={confirmModalConfiguration.actionToExecute}
                        setShowModal={setIsConfirmModalOpen}
                    />
                )}

                {/* QR INSTRUCTIONS MODAL */}
                {isQrModalOpen && (
                    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1060 }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                                <div className="modal-header bg-dark text-white border-0 py-3 px-4">
                                    <h5 className="modal-title fw-bold mb-0">
                                        <i className="bi bi-qr-code-scan text-warning me-2"></i> Conexión a Rasca y Gana
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        aria-label="Cerrar"
                                        onClick={() => setIsQrModalOpen(false)}
                                    ></button>
                                </div>
                                <div className="modal-body p-4 bg-light">
                                    <div className="mb-4">
                                        <PremiumQR url={fanJoinQrUrl} title="ÚNETE AL RASCA Y GANA" />
                                    </div>

                                    <div className="p-3 bg-white rounded-3 border">
                                        <label className="form-label fw-bold small text-dark mb-1">
                                            <i className="bi bi-tv me-1 text-primary"></i> Pantalla de Estadio / Overlays:
                                        </label>
                                        <div className="input-group input-group-sm mb-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={screenTargetDisplayUrl}
                                                readOnly
                                            />
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(screenTargetDisplayUrl);
                                                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Enlace de pantalla copiado al portapapeles.' });
                                                }}
                                            >
                                                Copiar
                                            </button>
                                            <button
                                                className="btn btn-primary"
                                                type="button"
                                                onClick={() => window.open(screenTargetDisplayUrl, '_blank')}
                                            >
                                                Abrir
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer bg-light border-0 py-2 px-4">
                                    <button
                                        type="button"
                                        className="btn btn-secondary w-100 fw-bold"
                                        onClick={() => setIsQrModalOpen(false)}
                                    >
                                        Cerrar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </SecondaryBase>
    );
};
